import { DataStore } from "@/datastore";
import type { EventStructure } from "./types";
import { getSessionScore } from "./session-score";
import { attachSessionDescription } from "./session-description";
import { ScoringHandler } from "@/dataprocessor/scoring";
import { preparePayload } from "@/inference/utils/payload";
import { demoSpecificOverrides } from "./demo-specific-overrides";
import { InferenceSetup } from "@/inference/setup";
import { Memory } from "@/memory";

export function compileEvents(): EventStructure {
  const response: EventStructure = {
    orgId: window.helium.brandData.orgId,
    storeId: window.helium.brandData.storeId ?? "",

    sessionId:
      window.helium.sessionToken ??
      sessionStorage.getItem("sessionToken") ??
      "",
    userId: window.helium.userToken ?? localStorage.getItem("userToken") ?? "",

    hostname: window.location.hostname,
    pathname: window.location.pathname,
    searchParams: window.location.search,

    sessionScore: getSessionScore(),

    inferredProducts: ScoringHandler.Product.getComputedProductScores(),

    sessionDescription: {
      behavior: "",
      demographic: "",
    },

    utm: window.location.search
      .replace("?", "")
      .split("&")
      .reduce(
        (acc, param) => {
          const [key, value] = param.split("=");

          if (!key.includes("utm")) return acc;

          acc[key] = value;
          return acc;
        },
        {} as Record<string, string>,
      ),

    meta: {
      timestamp: Date.now(),
      referrer: document.referrer,
      memory: {
        history: DataStore.memoryStore.getTotalWithHistory(),
      },
      landingTime: InferenceSetup.landingTimestamp(),
    },

    journey: window.helium.logs || [],

    demographic: window.helium.attributes.strongestInference || {},

    inferredTags: DataStore.getCleanNestedTags() || {},

    behavior: {
      inference: window.helium?.attributes?.fromBehavior?.inference || {},
      strongestInference:
        window.helium?.attributes?.fromBehavior?.strongestInference || {},
      attributes: Object.assign(
        {},
        window.helium.attributes.behavior.secondary,
        window.helium.attributes.behavior.tertiary,
      ),
    },

    ecommerce: {
      attributes: Object.assign(
        {},
        window.helium.attributes.ecommerce.primary,
        window.helium.attributes.ecommerce.secondary,
      ),
      inference: window.helium.attributes.fromEcommerce?.inference || {},
      strongestInference:
        window.helium.attributes.fromEcommerce?.strongestInference || {},
    },

    persona: {
      attributes: Object.assign(
        {},
        window.helium.attributes.persona.primary,
        window.helium.attributes.persona.secondary,
      ),
      inference: window.helium?.attributes?.fromPersona?.inference || {},
      strongestInference:
        window.helium?.attributes?.fromPersona?.strongestInference || {},
    },
  };

  const eventWithSessionDescription = attachSessionDescription(response);

  demoSpecificOverrides(eventWithSessionDescription);

  window.helium.finalResponse = eventWithSessionDescription;

  const { filteredPayload } = preparePayload(eventWithSessionDescription, true);

  Memory.persist(eventWithSessionDescription);

  sessionStorage.setItem("helium_event", JSON.stringify(filteredPayload ?? {}));

  return eventWithSessionDescription;
}
